import { baseWeb } from '../platform/enderecoWeb';

/**
 * Vocabulário da validade na tela — funções puras.
 *
 * Ficam separadas do componente porque são a parte que erra em silêncio: cor,
 * texto e ordenação de "vence hoje" não quebram nada visível quando estão
 * erradas, só fazem a confeiteira olhar para a geladeira errada.
 *
 * A CLASSIFICAÇÃO EM SI VEM DO SERVIDOR (`situacao` no payload). Aqui não se
 * recalcula nada a partir de datas: se a tela decidisse por conta própria o
 * que é "hoje", ela e a etiqueta impressa poderiam discordar — e o papel é o
 * que a pessoa tem na mão.
 */

export const SITUACOES = ['vencida', 'hoje', 'amanha', 'ok'];

const APARENCIA = {
  vencida: { rotulo: 'Vencida', cor: '#c0392b', fundo: '#fdecea', peso: 0 },
  hoje: { rotulo: 'Vence hoje', cor: '#b9770e', fundo: '#fef5e7', peso: 1 },
  amanha: { rotulo: 'Vence amanhã', cor: '#b9770e', fundo: '#fef9e7', peso: 2 },
  ok: { rotulo: 'Em dia', cor: '#1e8449', fundo: '#eafaf1', peso: 3 },
};

const NEUTRO = { rotulo: '—', cor: '#7f8c8d', fundo: '#f4f6f6', peso: 4 };

/** Cor, fundo e rótulo de uma situação. Nunca devolve undefined. */
export function aparenciaDaSituacao(situacao) {
  return APARENCIA[situacao] || NEUTRO;
}

export const ROTULO_CONSERVACAO = {
  ambiente: 'Temp. ambiente',
  refrigerado: 'Refrigerado',
  congelado: 'Congelado',
  descongelado: 'Descongelado',
};

/**
 * Ordena para a tela: o que vence antes aparece antes.
 *
 * Empatando a situação, desempata pela data — duas etiquetas "vence hoje" não
 * podem trocar de lugar a cada carregamento, senão a lista fica inquieta e a
 * pessoa perde a referência de onde estava.
 */
export function ordenarPorUrgencia(etiquetas) {
  return [...(etiquetas || [])].sort((a, b) => {
    const pa = aparenciaDaSituacao(a.situacao).peso;
    const pb = aparenciaDaSituacao(b.situacao).peso;
    if (pa !== pb) return pa - pb;
    return String(a.validade || '').localeCompare(String(b.validade || ''));
  });
}

/**
 * "restam 9 de 12" — só quando a fornada tem mais de uma unidade.
 *
 * Numa etiqueta de um pote só, "restam 1 de 1" é ruído puro.
 */
export function textoDoSaldo(etiqueta) {
  if (!etiqueta || !etiqueta.unidades || etiqueta.unidades <= 1) return null;
  return `restam ${etiqueta.unidades_restantes} de ${etiqueta.unidades}`;
}

/**
 * Converte para Date tratando data-sem-hora como LOCAL.
 *
 * `new Date('2027-01-31')` é interpretado pelo JS como meia-noite **UTC** —
 * que em São Paulo é 30/01 às 21h. A validade do fabricante vem do banco como
 * data pura, então ela era impressa **um dia antes** em toda etiqueta que a
 * carregava. Um dia a menos numa validade não quebra nada visível; só faz
 * jogar comida boa fora, ou desconfiar do sistema.
 */
function paraData(iso) {
  if (!iso) return null;
  const soData = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso));
  const d = soData
    ? new Date(Number(soData[1]), Number(soData[2]) - 1, Number(soData[3]))
    : new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Data curta para a lista, no fuso de quem está lendo. */
export function dataCurta(iso) {
  const d = paraData(iso);
  return d ? d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }) : '';
}

/**
 * Data por extenso, com o ano — para a tabela de lotes.
 *
 * POR QUE NAO A `dataCurta`: na lista de etiquetas o ano é ruído, porque quase
 * tudo ali vence nesta semana. Na tabela de estoque não: congelado de 90 dias
 * e farinha de dois anos convivem na mesma coluna, e "27/08" sem ano não diz
 * se é este ano ou o que vem.
 */
export function dataCompleta(iso) {
  const d = paraData(iso);
  return d ? d.toLocaleDateString('pt-BR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
  }) : '';
}

/**
 * Data com ano E hora — o formato do papel quando o prazo é curto.
 *
 * Na etiqueta impressa toda data leva o ano, inclusive esta. Misturar formatos
 * na mesma etiqueta (uma data com ano, outra sem) parece defeito de impressão
 * para quem lê, e o papel fica meses colado no pote.
 */
export function dataCompletaComHora(iso) {
  const d = paraData(iso);
  if (!d) return '';
  return d.toLocaleString('pt-BR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  }).replace(',', '');
}

/** Data com hora, para quando o prazo é curto e a hora importa. */
export function dataComHora(iso) {
  const d = paraData(iso);
  if (!d) return '';
  return d.toLocaleString('pt-BR', {
    day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
  }).replace(',', ' ·');
}

/**
 * Monta o que `platform/etiqueta.js` precisa para desenhar o papel.
 *
 * Existe para que a tela, o modal e a reimpressão produzam a MESMA etiqueta —
 * três lugares montando esse objeto na mão é onde uma delas sairia sem o lote.
 */
export function paraImpressao(etiqueta, { baseUrl } = {}) {
  const curto = etiqueta.prazo_dias != null && etiqueta.prazo_dias <= 2;
  // A base vem de platform/enderecoWeb: no app, window.location.origin seria
  // `capacitor://localhost` e o QR nasceria ilegivel para qualquer camera.
  const raiz = baseUrl || baseWeb();
  return {
    nome: etiqueta.nome,
    conservacao: etiqueta.conservacao,
    quantidade: etiqueta.quantidade != null
      ? String(etiqueta.quantidade).replace('.', ',')
      : null,
    unidade: etiqueta.unidade_medida,
    // Com ANO, sempre. Na lista o ano é ruído porque quase tudo vence nesta
    // semana; no papel não: um congelado de 90 dias fica no freezer atravessando
    // a virada do ano, e "22/11" sozinho não diz de qual ano é.
    manipulado: curto ? dataCompletaComHora(etiqueta.manipulado_em) : dataCompleta(etiqueta.manipulado_em),
    validade: curto ? dataCompletaComHora(etiqueta.validade) : dataCompleta(etiqueta.validade),
    // Quando a etiqueta mantem a validade do fabricante, as duas datas sao a
    // mesma — imprimir "VAL. ORIG." repetindo o que ja esta em corpo grande
    // gasta uma das duas linhas do rodape com nada.
    // A comparação também é com ano: 31/12/2026 e 31/12/2027 têm o mesmo
    // "31/12", e sem o ano a data do fabricante sumiria por engano.
    validadeOriginal: (etiqueta.validade_original
      && dataCompleta(etiqueta.validade_original) !== dataCompleta(etiqueta.validade))
      ? dataCompleta(etiqueta.validade_original) : null,
    lote: etiqueta.lote_texto,
    responsavel: etiqueta.responsavel,
    codigo: etiqueta.codigo,
    url: `${raiz}/e/${etiqueta.codigo}`,
  };
}

/**
 * Custo pronto para entrar num `<input type="number" step="0.01">`.
 *
 * POR QUE ARREDONDAR ANTES DE MOSTRAR: a média ponderada das entradas produz
 * valores como `10.984444444444446`. O input tem passo de um centavo, então o
 * navegador RECUSA o próprio valor que a tela preencheu — "os dois valores
 * válidos mais próximos são 10,98 e 10,99" — e a pessoa não consegue salvar nem
 * alterando só o nome do ingrediente. A mensagem não menciona custo, e o botão
 * simplesmente não responde.
 *
 * Vazio continua vazio: zero é um preço, vazio é "ainda não sei", e a ficha
 * técnica trata os dois de forma diferente.
 */
export function custoParaFormulario(valor) {
  if (valor === null || valor === undefined || valor === '') return '';
  const n = Number(valor);
  return Number.isFinite(n) ? n.toFixed(2) : '';
}

/**
 * Qual lote a etiqueta de embalagem aberta deve levar.
 *
 * O CASO QUE ISTO RESOLVE: a saída de estoque APAGA o lote quando ele chega a
 * zero. Então, justamente quando a etiqueta mais importa — "tirei os 2 últimos
 * açúcares e sobrou a embalagem aberta" —, o lote já não existe para a sugestão
 * encontrar, e o papel sairia sem número de lote e sem a validade do
 * fabricante.
 *
 * A saída sabe o que consumiu, e devolve isso. Quando ela consome mais de um
 * lote, vale o ÚLTIMO: o FIFO come do mais velho para o mais novo, e a
 * embalagem que ficou aberta na mão dela é a do último tocado.
 *
 * Sem lote em lugar nenhum a etiqueta ainda sai, só sem a data do fabricante —
 * perder a etiqueta seria pior que perder a data.
 */
export function loteParaEtiqueta(sugestao, consumidos) {
  if (sugestao?.lote_texto) {
    return {
      lote_texto: sugestao.lote_texto,
      validade_original: sugestao.validade_original || null,
    };
  }
  const lista = Array.isArray(consumidos) ? consumidos : [];
  const ultimo = lista.length ? lista[lista.length - 1] : null;
  return {
    lote_texto: ultimo?.numero_lote || '',
    validade_original: ultimo?.validade || null,
  };
}

/**
 * Tira do formulário tudo que pertence a um produto do estoque.
 *
 * POR QUE EXISTE: o modal abre com um produto (embalagem aberta), mas o select
 * "Ficha técnica" fica visível o tempo todo. Trocar para uma ficha sem limpar
 * levaria o lote e a validade do fabricante daquele produto para uma etiqueta
 * de preparação — impresso no papel, e invisível para quem lê o pote depois.
 *
 * Devolve um objeto novo: `form` é estado do React e mutar no lugar esconde a
 * mudança da tela.
 */
export function semVinculoDeProduto(form) {
  return {
    ...(form || {}),
    produto_id: null,
    lote_id: null,
    lote_texto: '',
    validade_original: null,
    lotes: [],
  };
}

/** Repete a etiqueta uma vez por unidade da fornada — 12 potes, 12 papéis. */
export function copiasParaFornada(etiqueta, opcoes) {
  const modelo = paraImpressao(etiqueta, opcoes);
  const quantas = Math.max(1, Number(etiqueta.unidades) || 1);
  return Array.from({ length: quantas }, () => modelo);
}
